import {makeAutoObservable, observable, runInAction} from 'mobx';

import {i18n} from '@yourcoach/i18n';

import type {NotificationEvent} from '../../../../../api/event';
import type {PaymentExpandedCommon} from '../../../../../api/payment';
import type {CoreClientPaymentsListParams} from '../../../../../api/types/core/methods';
import type {CollectionStore} from '../../../../../api/utils/collectionStore';
import type {Destructible} from '../../../../../utils/store';
import type {RefundSuccessEventData} from '../../../../payments/stores/MakeRefund/types';
import type {RenderContract} from '../../NotificationsListItemDumb/types';
import type {
  AsyncEventDataProvider,
  EventContextsMap,
  EventData,
} from '../types';

import {createCollectionStore} from '../../../../../api/utils/collectionStore';
import {AppError} from '../../../../../utils/errors/app';
import {emitter} from '../../../../../utils/eventEmitter';
import {MAKE_REFUND_SUCCESS_EVENT} from '../../../../payments/stores/MakeRefund/utils';

export class AsyncEventStore implements AsyncEventDataProvider, Destructible {
  data: RenderContract;
  private readonly paymentsStore: CollectionStore<
    PaymentExpandedCommon,
    CoreClientPaymentsListParams
  >;

  constructor(
    public event: NotificationEvent,
    public contexts: EventContextsMap,
    public staticData: RenderContract,
  ) {
    this.data = staticData;

    this.paymentsStore = createCollectionStore({
      method: 'coach.payments.list',
    });

    makeAutoObservable(
      this,
      {
        contexts: observable.ref,
        event: observable.ref,
        staticData: observable.ref,
      },
      {
        autoBind: true,
      },
    );

    this.updateData();
    this.subscribeToRefundEvent();
  }

  destruct() {
    this.unsubscribeFromRefundEvent();
  }

  private fetchPayments() {
    const {actor, course} = this.contexts;

    if (!course || !actor?._id) {
      throw new AppError(
        'Cannot fetch payments: course or user is not defined in context',
      );
    }

    return this.paymentsStore.fetch({
      query: [
        ['course_id', '==', course._id],
        ['user_id', '==', actor._id],
      ],
    });
  }

  private getButtons(isRefundAvailable: boolean): EventData['buttons'] {
    const {buttons = []} = this.staticData;
    const refundButton = buttons.find(({id}) => id === 'refund_button');

    if (refundButton) {
      if (isRefundAvailable) {
        refundButton.disabled = false;
      } else {
        refundButton.disabled = true;
        refundButton.label = i18n.t('shared:refunded');
      }
    }

    return buttons;
  }

  private async isSomePaymentRefunded() {
    const payments = await this.fetchPayments();

    return payments.some(({refunded_amount}) => refunded_amount > 0);
  }

  private refundSuccessHandler({payment}: RefundSuccessEventData) {
    if (this.contexts.actor?._id === payment.user_id) {
      this.updateData();
    }
  }

  private subscribeToRefundEvent() {
    emitter.on(MAKE_REFUND_SUCCESS_EVENT, this.refundSuccessHandler);
  }

  private unsubscribeFromRefundEvent() {
    emitter.off(MAKE_REFUND_SUCCESS_EVENT, this.refundSuccessHandler);
  }

  private async updateData() {
    const isRefundAvailable = !(await this.isSomePaymentRefunded());

    if (!isRefundAvailable) {
      this.unsubscribeFromRefundEvent();
    }

    runInAction(() => {
      this.data = {
        ...this.staticData,
        buttons: this.getButtons(isRefundAvailable),
      };
    });
  }
}
